import {
	DataGrid,
	GridColDef,
	GridEventListener,
	GridToolbar,
	GridPaginationModel,
	GridSortModel,
	GridFilterModel,
	GridSlots,
	GridSlotProps,
	GridRowHeightParams,
	type GridValidRowModel,
} from '@mui/x-data-grid';

export interface BaseDataGridProps {
	rows: GridValidRowModel[];
	columns: GridColDef[];
	loading?: boolean;
	ariaLabel?: string;
	onRowClick?: GridEventListener<'rowClick'>;
	paginationModel?: GridPaginationModel;
	onPaginationModelChange?: (model: GridPaginationModel) => void;
	sortModel?: GridSortModel;
	onSortModelChange?: (model: GridSortModel) => void;
	filterModel?: GridFilterModel;
	onFilterModelChange?: (model: GridFilterModel) => void;
	rowCount?: number;
	paginationMode?: 'client' | 'server';
	sortingMode?: 'client' | 'server';
	filterMode?: 'client' | 'server';
	getRowId?: (row: GridValidRowModel) => string | number;
	pageSizeOptions?: number[];
	disableRowSelectionOnClick?: boolean;
	rowHeight?: number;
	getRowHeight?: (params: GridRowHeightParams) => number | null | 'auto';
	slots?: Partial<GridSlots>;
	slotProps?: Partial<GridSlotProps>;
}

export default function BaseDataGrid({
	rows,
	columns,
	loading = false,
	ariaLabel,
	onRowClick,
	paginationModel,
	onPaginationModelChange,
	sortModel,
	onSortModelChange,
	filterModel,
	onFilterModelChange,
	rowCount,
	paginationMode = 'client',
	sortingMode = 'client',
	filterMode = 'client',
	getRowId,
	pageSizeOptions,
	disableRowSelectionOnClick,
	rowHeight,
	getRowHeight,
	slots,
	slotProps,
}: BaseDataGridProps) {
	return (
		<DataGrid
			aria-label={ariaLabel}
			rows={rows}
			columns={columns}
			loading={loading}
			onRowClick={onRowClick}
			paginationModel={paginationModel}
			onPaginationModelChange={onPaginationModelChange}
			sortModel={sortModel}
			onSortModelChange={onSortModelChange}
			filterModel={filterModel}
			onFilterModelChange={onFilterModelChange}
			rowCount={rowCount}
			paginationMode={paginationMode}
			sortingMode={sortingMode}
			filterMode={filterMode}
			getRowId={getRowId}
			pageSizeOptions={pageSizeOptions}
			disableRowSelectionOnClick={disableRowSelectionOnClick}
			rowHeight={rowHeight}
			getRowHeight={getRowHeight}
			localeText={{ noRowsLabel: 'Chưa có dữ liệu' }}
			slots={slots || { toolbar: GridToolbar }}
			slotProps={slotProps || {
				toolbar: {
					showQuickFilter: true,
					quickFilterProps: { debounceMs: 500 },
				},
			}}
			sx={{
				height: '100%',
				'& .MuiDataGrid-main': {
					borderRadius: '8px',
				},
				'& .MuiDataGrid-columnHeaders': {
					backgroundColor: 'grey.100',
					color: 'text.primary',
					borderRadius: '8px 8px 0 0',
					minHeight: '56px !important',
				},
				'& .MuiDataGrid-columnHeaderTitle': {
					fontWeight: 'bold',
					fontSize: '0.95rem',
				},
				'& .MuiDataGrid-cell': {
					display: 'flex',
					alignItems: 'center',
					borderBottom: '1px solid rgba(224, 224, 224, 1)',
					fontSize: '0.875rem',
				},
				'& .MuiDataGrid-row': {
					'&:nth-of-type(even)': {
						backgroundColor: 'grey.50',
					},
					'&:hover': {
						backgroundColor: 'action.hover !important',
						cursor: 'pointer',
					},
					'&:nth-of-type(even):hover': {
						backgroundColor: 'action.hover !important',
					},
				},
				'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
					'&:focus-within': {
						outline: '2px solid',
						outlineColor: 'primary.main',
						outlineOffset: '-2px',
					},
				},
				'& .MuiDataGrid-footerContainer': {
					borderTop: '2px solid',
					borderColor: 'primary.main',
					backgroundColor: 'grey.50',
					overflow: 'hidden',
				},
				'& .MuiTablePagination-root': {
					overflow: 'hidden',
				},
			}}
		/>
	);
}